import { forwardRef, useState } from "react";
import type { InputProps } from "./Input";
import {
  createMoneyChangeEvent,
  formatMoneyDisplay,
  moneyValueToDecimalString,
  parseMoneyValue,
} from "@/lib/moneyInput";

export type MoneyInputProps = Omit<InputProps, "type">;

const Skeleton = ({ className }: { className: string }) => (
  <div className={`animate-pulse bg-primary-100 dark:bg-primary-800 rounded ${className}`} />
);

const variantStyles = {
  primary: {
    label: "block text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-2",
    input:
      "px-4 py-3 border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-950 text-primary-950 dark:text-white focus:outline-none focus:border-primary-400 transition-colors duration-300",
    error: "mt-1 text-sm text-red-600 dark:text-red-400",
  },
  secondary: {
    label: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2",
    input:
      "appearance-none relative block px-4 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 transition duration-150",
    error: "mt-1 text-sm text-red-600 dark:text-red-400",
  },
};

export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    {
      label,
      error,
      variant = "primary",
      fullWidth = true,
      loading = false,
      id,
      value = "",
      onChange,
      onBlur,
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    const styles = variantStyles[variant];
    const inputId = id || `money-input-${Math.random().toString(36).substring(2, 11)}`;
    const [draftDisplay, setDraftDisplay] = useState<string | null>(null);
    const displayValue = draftDisplay ?? formatMoneyDisplay(String(value));

    const emitChange = (decimalString: string) => {
      onChange?.(createMoneyChangeEvent(decimalString));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = parseMoneyValue(event.target.value);
      if (Number.isNaN(parsed)) {
        setDraftDisplay("");
        emitChange("");
        return;
      }

      const decimalString = moneyValueToDecimalString(parsed);
      setDraftDisplay(formatMoneyDisplay(parsed));
      emitChange(decimalString);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const parsed = parseMoneyValue(displayValue);
      if (!Number.isNaN(parsed)) {
        const decimalString = moneyValueToDecimalString(parsed);
        emitChange(decimalString);
      }
      setDraftDisplay(null);
      onBlur?.(event);
    };

    if (loading) {
      return (
        <div>
          {label && (
            <Skeleton
              className={`${variant === "primary" ? "h-4 w-24 mb-2" : "h-5 w-32 mb-2"}`}
            />
          )}
          <Skeleton className="h-12 w-full" />
          {error && <Skeleton className="h-4 w-48 mt-1" />}
        </div>
      );
    }

    return (
      <div>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className={`${styles.input} ${fullWidth ? "w-full" : ""}`}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          aria-invalid={!!error}
          {...props}
        />
        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
  }
);

MoneyInput.displayName = "MoneyInput";
